<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import segueDashboardAPI from 'dashboard/api/segueDashboard';
import ConversaModal from './ConversaModal.vue';

const props = defineProps({
  period: { type: String, default: 'today' },
  from: { type: String, default: null },
  to: { type: String, default: null },
  bank: { type: String, default: 'all' },
  refreshKey: { type: Number, default: 0 },
});

const loading = ref(false);
const contratos = ref([]);
const warnings = ref([]);
const errorMsg = ref('');
const copiedIdx = ref(null);

const modalPhone = ref('');
const modalNome = ref('');
const showModal = ref(false);

const total = computed(() => contratos.value.length);

const fmtMoney = v => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Aceita ISO (YYYY-MM-DD...) e dd/mm/yyyy; devolve dd/mm/yyyy.
const fmtDate = v => {
  if (!v) return '—';
  const s = String(v);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
};

const statusClass = status => {
  const s = (status || '').toString().toLowerCase();
  if (s.includes('pag')) return 'bg-green-100 text-green-800';
  if (s.includes('cancel') || s.includes('reprov'))
    return 'bg-red-100 text-red-800';
  if (s.includes('anal') || s.includes('aguard') || s.includes('process'))
    return 'bg-amber-100 text-amber-800';
  return 'bg-n-solid-2 text-n-slate-12';
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  warnings.value = [];
  try {
    const { data } = await segueDashboardAPI.fetchContratos({
      period: props.period,
      from: props.from,
      to: props.to,
      bank: props.bank,
    });
    contratos.value = data.contratos || [];
    warnings.value = data.warnings || [];
  } catch (e) {
    errorMsg.value = e.response?.data?.error || 'Erro ao carregar contratos.';
  } finally {
    loading.value = false;
  }
};

const copyLink = async (link, idx) => {
  if (!link) return;
  try {
    await navigator.clipboard.writeText(link);
  } catch (e) {
    const ta = document.createElement('textarea');
    ta.value = link;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
  copiedIdx.value = idx;
  setTimeout(() => {
    if (copiedIdx.value === idx) copiedIdx.value = null;
  }, 1500);
};

const openConversa = c => {
  if (!c.telefone) return;
  modalPhone.value = c.telefone;
  modalNome.value = c.nome || '';
  showModal.value = true;
};

watch(
  () => [props.period, props.from, props.to, props.bank, props.refreshKey],
  load
);
onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <section
    class="flex flex-col w-full rounded-2xl border border-n-weak bg-n-background"
  >
    <!-- Cabecalho -->
    <div class="flex justify-between items-center px-5 py-4 border-b border-n-weak">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">Contratos</h2>
        <p class="text-sm text-n-slate-11">
          {{ total }} contrato(s) · filtro pelo período do topo
        </p>
      </div>
      <span
        v-if="loading"
        class="text-sm font-medium text-n-slate-11"
      >
        Carregando…
      </span>
    </div>

    <div
      v-if="warnings.length"
      class="px-5 py-2 text-sm border-b border-yellow-200 bg-yellow-50 text-yellow-800"
    >
      {{ warnings.join(' · ') }}
    </div>

    <!-- Tabela -->
    <div class="overflow-x-auto">
      <table class="w-full text-sm text-left border-collapse">
        <thead>
          <tr
            class="text-xs font-semibold uppercase border-b text-n-slate-11 bg-n-alpha-1 border-n-weak"
          >
            <th class="px-3 py-2.5">Nome</th>
            <th class="px-3 py-2.5">CPF</th>
            <th class="px-3 py-2.5">Telefone</th>
            <th class="px-3 py-2.5">Banco</th>
            <th class="px-3 py-2.5">Status</th>
            <th class="px-3 py-2.5 text-right">Valor</th>
            <th class="px-3 py-2.5 text-right">Prazo</th>
            <th class="px-3 py-2.5 text-right">Parcela</th>
            <th class="px-3 py-2.5">Digitação</th>
            <th class="px-3 py-2.5">Pagamento</th>
            <th class="px-3 py-2.5">UUID</th>
            <th class="px-3 py-2.5">Link</th>
            <th class="px-3 py-2.5 text-center">Conversa</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="13" class="px-3 py-8 text-center text-n-slate-11">
              Carregando…
            </td>
          </tr>
          <tr v-else-if="errorMsg">
            <td colspan="13" class="px-3 py-8 text-center text-n-slate-11">
              {{ errorMsg }}
            </td>
          </tr>
          <tr v-else-if="!contratos.length">
            <td colspan="13" class="px-3 py-8 text-center text-n-slate-11">
              Nenhum contrato para este período/filtro.
            </td>
          </tr>
          <tr
            v-for="(c, i) in contratos"
            v-else
            :key="i"
            class="border-b border-n-weak hover:bg-n-alpha-1"
          >
            <td class="px-3 py-2.5 font-medium text-n-slate-12">
              {{ c.nome || '—' }}
            </td>
            <td class="px-3 py-2.5 text-n-slate-12">{{ c.cpf || '—' }}</td>
            <td class="px-3 py-2.5 text-n-slate-12">{{ c.telefone || '—' }}</td>
            <td class="px-3 py-2.5">
              <span
                class="px-2 py-0.5 text-xs font-semibold rounded-md bg-n-solid-2 text-n-slate-12"
              >
                {{ c.banco }}
              </span>
            </td>
            <td class="px-3 py-2.5">
              <span
                class="px-2 py-0.5 text-xs font-semibold rounded-md"
                :class="statusClass(c.status)"
              >
                {{ c.status || '—' }}
              </span>
            </td>
            <td class="px-3 py-2.5 font-medium text-right text-n-slate-12">
              {{ fmtMoney(c.valor_liberado) }}
            </td>
            <td class="px-3 py-2.5 text-right text-n-slate-12">
              {{ c.prazo || '—' }}
            </td>
            <td class="px-3 py-2.5 text-right text-n-slate-12">
              {{ fmtMoney(c.parcela) }}
            </td>
            <td class="px-3 py-2.5 text-n-slate-12">
              {{ fmtDate(c.data_digitacao) }}
            </td>
            <td class="px-3 py-2.5 text-n-slate-12">
              {{ fmtDate(c.data_pagamento) }}
            </td>
            <td class="px-3 py-2.5 text-xs text-n-slate-11">
              <span class="block max-w-[120px] truncate" :title="c.uuid">
                {{ c.uuid || '—' }}
              </span>
            </td>
            <td class="px-3 py-2.5">
              <button
                v-if="c.link_formalizacao"
                type="button"
                class="inline-flex gap-1.5 items-center px-2.5 h-8 text-xs font-semibold rounded-lg border text-n-slate-12 bg-n-solid-1 border-n-weak hover:bg-n-alpha-2"
                title="Copiar link de formalização"
                @click="copyLink(c.link_formalizacao, i)"
              >
                <span
                  :class="copiedIdx === i ? 'i-lucide-check' : 'i-lucide-copy'"
                  class="size-3.5"
                />
                {{ copiedIdx === i ? 'Copiado!' : 'Copiar link' }}
              </button>
              <span v-else class="text-xs text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 text-center">
              <button
                type="button"
                class="inline-flex justify-center items-center text-white bg-green-500 rounded-lg size-8 hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="!c.telefone"
                title="Ver conversa (somente leitura)"
                @click="openConversa(c)"
              >
                <span class="i-lucide-message-circle size-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ConversaModal
      v-if="showModal"
      :key="modalPhone"
      :phone="modalPhone"
      :nome="modalNome"
      @close="showModal = false"
    />
  </section>
</template>
